// src/components/billing/PricingPlans.jsx
// Sole-professional model: full access via the book (Amazon + redeem code),
// optional off-platform donation. No subscriptions / no direct billing.
import React from 'react';
import { Box, Typography, Button, Chip, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BOOK_URL, DONATION_URL } from '../../config';
import { useT } from '../../i18n/LanguageContext';

function Feature({ children, color }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mb: 1.2 }}>
      <CheckIcon sx={{ fontSize: 18, color, mt: '2px' }} />
      <Typography sx={{ fontSize: '0.95rem', color: 'text.secondary' }}>{children}</Typography>
    </Stack>
  );
}

function PlanCard({ children, featured, theme }) {
  return (
    <Box sx={{
      position: 'relative', flex: 1, display: 'flex', flexDirection: 'column',
      bgcolor: '#fff', borderRadius: 5, p: { xs: 3.5, md: 4 },
      border: featured ? `2px solid ${theme.home.cyanText}` : `1px solid ${theme.home.paperDeep}`,
      boxShadow: featured ? theme.home.softShadow : 'none',
      transform: { md: featured ? 'translateY(-12px)' : 'none' },
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    }}>
      {children}
    </Box>
  );
}

export default function PricingPlans() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isPro } = useAuth();
  const t = useT();

  const planName = (label) => (
    <Typography sx={{ fontFamily: theme.home.display, fontWeight: 600, fontSize: '1.5rem', color: theme.home.ink }}>{label}</Typography>
  );

  if (isPro) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Chip label={t('pricing.proChip')} sx={{ bgcolor: theme.home.cyanText, color: '#fff', fontWeight: 700, mb: 2 }} />
        <Typography sx={{ fontFamily: theme.home.display, fontWeight: 700, fontSize: '1.8rem', color: theme.home.ink, mb: 1 }}>
          {t('pricing.proTitle')}
        </Typography>
        <Typography sx={{ color: theme.home.inkMuted, mb: 3 }}>{t('pricing.proBody')}</Typography>
        <Button
          variant="outlined" component="a" href={DONATION_URL} target="_blank" rel="noopener noreferrer"
          sx={{ px: 3.5, py: 1.3, borderRadius: 999, borderColor: theme.home.coralText, color: theme.home.coralText,
            '&:hover': { borderColor: theme.home.coralText, bgcolor: 'rgba(255,106,77,0.06)' } }}
        >
          {t('pricing.proSupportButton')}
        </Button>
      </Box>
    );
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
      {/* Free */}
      <PlanCard theme={theme}>
        {planName(t('pricing.freeName'))}
        <Typography sx={{ fontFamily: theme.home.display, fontWeight: 900, fontSize: '2.6rem', color: theme.home.ink, my: 1 }}>
          {t('pricing.freePrice')}
        </Typography>
        <Typography sx={{ color: theme.home.inkMuted, mb: 3 }}>{t('pricing.freeLede')}</Typography>
        <Box sx={{ flexGrow: 1 }}>
          <Feature color={theme.home.inkMuted}>{t('pricing.freeFeature1')}</Feature>
          <Feature color={theme.home.inkMuted}>{t('pricing.freeFeature2')}</Feature>
          <Feature color={theme.home.inkMuted}>{t('pricing.freeFeature3')}</Feature>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/signup')}
          sx={{ mt: 2, borderRadius: 999, py: 1.2, borderColor: theme.home.ink, color: theme.home.ink }}>
          {t('pricing.freeCta')}
        </Button>
      </PlanCard>

      {/* Full access via the book — featured */}
      <PlanCard featured theme={theme}>
        <Chip label={t('pricing.bookChip')} size="small"
          sx={{ position: 'absolute', top: 18, right: 18, bgcolor: theme.home.coralText, color: '#fff', fontWeight: 700 }} />
        {planName(t('pricing.bookName'))}
        <Typography sx={{ fontFamily: theme.home.display, fontWeight: 900, fontSize: '2.2rem', color: theme.home.ink, my: 1 }}>
          {t('pricing.bookPriceLabel')}
        </Typography>
        <Typography sx={{ color: theme.home.inkMuted, mb: 2 }}>
          {t('pricing.bookLedePre')} <em>Sinergie Generative</em> {t('pricing.bookLedePost')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <Feature color={theme.home.cyanText}>{t('pricing.bookFeature1')}</Feature>
          <Feature color={theme.home.cyanText}>{t('pricing.bookFeature2')}</Feature>
          <Feature color={theme.home.cyanText}>{t('pricing.bookFeature3')}</Feature>
          <Feature color={theme.home.cyanText}>{t('pricing.bookFeature4')}</Feature>
        </Box>
        <Stack spacing={1.2} sx={{ mt: 2 }}>
          <Button fullWidth variant="contained" component="a" href={BOOK_URL} target="_blank" rel="noopener noreferrer"
            sx={{ py: 1.4, borderRadius: 999, bgcolor: theme.home.ink, '&:hover': { bgcolor: theme.home.inkHover }, boxShadow: theme.home.cardShadow }}>
            {t('pricing.bookCtaBuy')}
          </Button>
          <Button fullWidth variant="text" onClick={() => navigate('/redeem')}
            sx={{ borderRadius: 999, color: theme.home.ink }}>
            {t('pricing.bookCtaRedeem')}
          </Button>
        </Stack>
      </PlanCard>

      {/* Support */}
      <PlanCard theme={theme}>
        {planName(t('pricing.supportName'))}
        <Typography sx={{ fontFamily: theme.home.display, fontWeight: 900, fontSize: '2.6rem', color: theme.home.coralText, my: 1 }}>
          ♥
        </Typography>
        <Typography sx={{ color: theme.home.inkMuted, mb: 2 }}>
          {t('pricing.supportLede')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <Feature color={theme.home.coralText}>{t('pricing.supportFeature1')}</Feature>
          <Feature color={theme.home.coralText}>{t('pricing.supportFeature2')}</Feature>
        </Box>
        <Button fullWidth variant="outlined" component="a" href={DONATION_URL} target="_blank" rel="noopener noreferrer"
          sx={{ mt: 2, py: 1.4, borderRadius: 999, borderColor: theme.home.coralText, color: theme.home.coralText,
            '&:hover': { borderColor: theme.home.coralText, bgcolor: 'rgba(255,106,77,0.06)' } }}>
          {t('pricing.supportCta')}
        </Button>
      </PlanCard>
    </Stack>
  );
}
